import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { GraduationCap, MapPin, Calendar, Download, Filter } from "lucide-react";
import { useSection, useSeo } from "@/contexts/SiteContentContext";

const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];

const LEVEL_BADGE = {
  "NCAA D1": "bg-[#E63946] text-white",
  "NCAA D2": "bg-[#0A192F] text-white",
  "NCAA D3": "bg-indigo-600 text-white",
  "NAIA": "bg-emerald-600 text-white",
  "JUCO": "bg-amber-600 text-white",
  "U Sports": "bg-sky-700 text-white",
  "Pro": "bg-purple-700 text-white",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function Commitments() {
  const sc = useSection("commitments");
  useSeo(sc.seo_title, sc.meta_description);
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [f, setF] = useState({ year: "", graduation_year: "", province: "", primary_position: "", level: "", school: "" });

  const load = useCallback(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = k === "year" || k === "graduation_year" ? Number(v) : v; });
    api.get("/commitments", { params }).then(({ data }) => setData(data)).catch(() => setData({ items: [], total: 0, facets: {} }));
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const facets = data?.facets || {};
  const items = data?.items || [];

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white border-b-4 border-[#E63946]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
          <div className="flex items-center gap-2 label-overline text-[#E63946]"><GraduationCap className="w-4 h-4" /> {sc.overline || "Recruiting"}</div>
          <h1 data-testid="commitments-title" className="font-display text-4xl sm:text-6xl uppercase tracking-tighter mt-2">{sc.title || "College Commitments"}</h1>
          <p className="text-white/60 text-sm mt-3 max-w-2xl">{sc.subtitle || "Canadian prospects taking their game to the next level. Every commitment below is verified and published by CBR staff."}</p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-slate-500 mb-2 sm:hidden"><Filter className="w-4 h-4" /><span className="label-overline">Filters</span></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <select data-testid="commit-filter-level" value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">All levels</option>
              {(facets.levels || []).map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select data-testid="commit-filter-year" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">Commit year</option>
              {(facets.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select data-testid="commit-filter-grad" value={f.graduation_year} onChange={(e) => setF({ ...f, graduation_year: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">Grad year</option>
              {(facets.grad_years || []).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select data-testid="commit-filter-province" value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">All provinces</option>
              {(facets.provinces || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select data-testid="commit-filter-position" value={f.primary_position} onChange={(e) => setF({ ...f, primary_position: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">All positions</option>
              {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select data-testid="commit-filter-school" value={f.school} onChange={(e) => setF({ ...f, school: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">All schools</option>
              {(facets.schools || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-slate-500" data-testid="commitments-count">{items.length} commitment{items.length !== 1 ? "s" : ""}</span>
            {isAdmin && (
              <button data-testid="commitments-export-csv" onClick={() => downloadFile("/admin/commitments/export.csv", {}, "cbr-commitments.csv")}
                className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946]">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {items.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-sm p-12 text-center">
              <GraduationCap className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">No commitments match your filters yet.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((c) => (
                <Link key={c.id} to={`/prospects/${c.slug}`} data-testid={`commitment-card-${c.id}`}
                  className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-4 hover:border-[#0A192F] transition-colors group">
                  <div className="w-16 h-16 rounded-sm bg-slate-100 overflow-hidden shrink-0">
                    {c.profile_photo_url
                      ? <img src={c.profile_photo_url} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full grid place-items-center font-display text-2xl text-slate-300">{(c.first_name || "?")[0]}{(c.last_name || "")[0]}</div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold group-hover:text-[#E63946] truncate">{c.first_name} {c.last_name}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                      <span>{c.primary_position || "—"}</span>
                      <span>· {c.graduation_year || "—"}</span>
                      {c.province && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{c.province}</span>}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      {c.school_logo_url && <img src={c.school_logo_url} alt={c.school_name || ""} className="w-5 h-5 object-contain" />}
                      <span className="font-display uppercase tracking-tight text-sm text-[#0A192F] truncate">{c.school_name}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      {c.level && <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${LEVEL_BADGE[c.level] || "bg-slate-200"}`}>{c.level}</span>}
                      <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5"><Calendar className="w-3 h-3" />{fmtDate(c.commitment_date)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
